import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import "./Business.css"; // Custom CSS file
import {
  addBusiness,
  createBusiness,
  getBusinessById,
  updateBusiness,
} from "../../services/services";
import { toast } from "react-toastify";
import Loading from "../../components/loading";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Upload } from "lucide-react";
import { useDispatch } from "react-redux";
import {
  fetchBusiness,
  handleUpdateBusiness,
} from "../../redux/business/businessSlice";
import { countryStates } from "../../utils";
import { Button } from "react-bootstrap";

function AddBussiness() {
  const { id } = useParams();
  const [existingData, setExistingData] = useState(null);
  const [loading, setLoading] = useState(true);

  const storeUser = JSON.parse(localStorage.getItem("Login_User"));
  const userId = storeUser?._id;
  const [availableStates, setAvailableStates] = useState([]);

  const [formData, setFormData] = useState({
    user: userId,
    logo: "",
    businessName: "",
    phone: "",
    gstNumber: "",
    email: "",
    businessType: "",
    category: "",
    country: "India",
    state: "",
    city: "",
    pin: "",
    address: "",
    signature: null,
  });
  const businessCategories = [
    "Retail",
    "Grocery",
    "Clothing",
    "Jewellery",
    "Pharmacy",
    "Restaurant",
    "Services",
    "Other",
  ];

  const [previewLogo, setPreviewLogo] = useState(null);
  const [previewSignature, setPreviewSignature] = useState(null);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!id) {
      setLoading(false);
    }
  }, [id]);

  const fetchBusinessById = async () => {
    try {
      const res = await getBusinessById(id);
      if (res.data.status) {
        const data = res.data.response;
        setExistingData(data);

        setFormData({
          user: userId,
          logo: data.logo || "",
          businessName: data.businessName || "",
          phone: data.phone || "",
          gstNumber: data.gstNumber || "",
          email: data.email || "",
          businessType: data.businessType || "",
          category: data.category || "",
          country: data.country || "India",
          state: data.state || "",
          city: data.city || "",
          pin: data.pin || "",
          address: data.address || "",
          signature: data.signature || null,
        });

        if (data.logo)
          setPreviewLogo(
            `${process.env.REACT_APP_IMAGE_URL}/uploads/business/${data.logo}`,
          );
        if (data.signature)
          setPreviewSignature(
            `${process.env.REACT_APP_IMAGE_URL}/uploads/business/${data.signature}`,
          );

        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchBusinessById();
  }, [id]);

  useEffect(() => {
    if (formData.country) {
      setAvailableStates(countryStates[formData.country] || []);
    }
  }, [formData.country]);

  const handleChange = (field, value) => {
    let newValue = value;

    // GST → uppercase + max 15
    if (field === "gstNumber") {
      newValue = value.toUpperCase().slice(0, 15);
    }

    // Phone → only digits, max 10
    if (field === "phone") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, [field]: newValue }));

    // remove error on typing
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleCountryChange = (country) => {
    handleChange("country", country);
    handleChange("state", ""); // reset state

    setAvailableStates(countryStates[country] || []);
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange(field, file);
      const preview = URL.createObjectURL(file);
      if (field === "logo") setPreviewLogo(preview);
      else setPreviewSignature(preview);
    }
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.businessName)
      newErrors.businessName = "Business Name is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.pin) {
      newErrors.pin = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pin)) {
      newErrors.pin = "Pincode must be exactly 6 digits";
    }
    if (formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "Enter valid 10 digit mobile number";
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter valid email address";
    }

    // GST (15 chars + format)
    if (
      formData.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.gstNumber,
      )
    ) {
      newErrors.gstNumber = "Enter valid 15 character GSTIN";
    }

    setErrors(newErrors);

    // Show only first error toast
    const firstError = Object.values(newErrors)[0];
    if (firstError) toast.error(firstError);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    setLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key]) formDataToSend.append(key, formData[key]);
    });

    let res;

    try {
      if (id) {
        // res = await updateBusiness(id, formDataToSend);
        await dispatch(
          handleUpdateBusiness({ id, data: formDataToSend }),
        ).unwrap();

        toast.success("Business updated successfully");
        navigate("/business");
      } else {
        res = await createBusiness(formDataToSend);
        if (res.data.status) {
          toast.success(res.data.message);
          await dispatch(fetchBusiness(userId));
          navigate("/business");
        } else {
          toast.error(res.data.response || res.data.message);
        }
      }
    } catch (error) {
      toast.error(error.response.data.response || error.response.data.message);
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div id="main">
          <div className="d-flex align-items-center mb-4 gap-3">
            <Link to="/business" className="text-decoration-none">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h1 className="h4 fw-bold mb-0 ">
              {existingData ? "Edit Business" : "Add Business"}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="card">
              <div className="card-body form-card-body">
                {/* Logo Upload */}
                <div className="d-flex flex-column align-items-center mb-5 logo-section">
                  <div className="logo-upload-container">
                    {previewLogo ? (
                      <img
                        src={previewLogo}
                        alt="Logo Preview"
                        className="logo-preview"
                      />
                    ) : (
                      <span className="logo-placeholder">Add Logo</span>
                    )}
                    <label className="logo-upload-label">
                      <Upload size={16} />
                      <input
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => handleFileChange("logo", e)}
                      />
                    </label>
                  </div>
                </div>

                {/* Business Details */}
                <div className="row g-4">
                  {/* LEFT SIDE */}
                  <div className="col-md-8">
                    <div className="row g-3">
                      {/* Business Name */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            Business Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.businessName ? "is-invalid" : ""}`}
                            value={formData.businessName}
                            onChange={(e) =>
                              handleChange("businessName", e.target.value)
                            }
                            placeholder="Enter Business Name"
                          />
                          {errors.businessName && (
                            <small className="text-danger">
                              {errors.businessName}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Phone Number</label>
                          <input
                            type="tel"
                            className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                            value={formData.phone}
                            onChange={(e) =>
                              handleChange("phone", e.target.value)
                            }
                          />
                          {errors.phone && (
                            <div className="invalid-feedback">
                              {errors.phone}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* GSTIN */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">GSTIN</label>
                          <input
                            type="text"
                            className={`form-control ${errors.gstNumber ? "is-invalid" : ""}`}
                            value={formData.gstNumber}
                            onChange={(e) =>
                              handleChange("gstNumber", e.target.value)
                            }
                          />
                          {errors.gstNumber && (
                            <div className="invalid-feedback">
                              {errors.gstNumber}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Email ID</label>
                          <input
                            type="email"
                            className={`form-control ${errors.email ? "is-invalid" : ""}`}
                            value={formData.email}
                            onChange={(e) =>
                              handleChange("email", e.target.value)
                            }
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Business Type */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Business Type</label>
                          <select
                            className="form-select"
                            value={formData.businessType}
                            onChange={(e) =>
                              handleChange("businessType", e.target.value)
                            }
                          >
                            <option value="">Select Business Type</option>
                            <option value="sole">Sole Proprietorship</option>
                            <option value="partnership">Partnership</option>
                            <option value="pvt">Private Limited</option>
                            <option value="llp">LLP</option>
                          </select>
                        </div>
                      </div>

                      {/* Business Category */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            Business Category
                          </label>

                          <select
                            className="form-select"
                            value={formData.category}
                            onChange={(e) =>
                              handleChange("category", e.target.value)
                            }
                          >
                            <option value="">Select Business Category</option>

                            {businessCategories.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Country */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            Country
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${errors.country ? "is-invalid" : ""}`}
                            value={formData.country}
                            onChange={(e) =>
                              handleCountryChange(e.target.value)
                            }
                          >
                            <option value="">Select Country</option>
                            <option value="India">India</option>
                            <option value="USA">USA</option>
                            <option value="UK">UK</option>
                          </select>
                          {errors.country && (
                            <small className="text-danger">
                              {errors.country}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* State */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            State<span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${errors.state ? "is-invalid" : ""}`}
                            value={formData.state}
                            onChange={(e) =>
                              handleChange("state", e.target.value)
                            }
                            disabled={!formData.country}
                          >
                            <option value="">Select State</option>
                            {availableStates.map((state) => (
                              <option key={state} value={state}>
                                {state}
                              </option>
                            ))}
                          </select>
                          {errors.state && (
                            <small className="text-danger">
                              {errors.state}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* City */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            City
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.city ? "is-invalid" : ""}`}
                            value={formData.city}
                            onChange={(e) =>
                              handleChange("city", e.target.value)
                            }
                            placeholder="Enter City"
                          />
                          {errors.city && (
                            <small className="text-danger">{errors.city}</small>
                          )}
                        </div>
                      </div>
                      {/* Pin Code */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">
                            Pin Code
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.pin ? "is-invalid" : ""}`}
                            value={formData.pin}
                            maxLength={6}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (value.length <= 6) {
                                handleChange("pin", value);
                              }
                            }}
                            placeholder="Enter Pin Code"
                          />
                          {errors.pin && (
                            <small className="text-danger">{errors.pin}</small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="col-md-4">
                    <div className="d-flex flex-column gap-4">
                      {/* Address */}
                      <div className="form-group">
                        <label className="form-label">Business Address</label>
                        <textarea
                          className="form-control"
                          value={formData.address}
                          onChange={(e) =>
                            handleChange("address", e.target.value)
                          }
                          placeholder="Enter Business Address"
                          rows={5}
                          style={{ resize: "none" }}
                        />
                      </div>

                      {/* Signature */}
                      <div className="form-group">
                        <div className="signature-upload-container">
                          {previewSignature ? (
                            <img
                              src={previewSignature}
                              alt="Signature Preview"
                              className="signature-preview"
                            />
                          ) : (
                            <div className="signature-placeholder">
                              <Upload size={16} />
                              <span>Upload Signature</span>
                            </div>
                          )}

                          <label
                            htmlFor="signatureUpload"
                            className="signature-upload-label"
                          >
                            Choose File
                          </label>

                          <input
                            type="file"
                            accept="image/*"
                            className="d-none  ,signature-upload-label   "
                            id="signatureUpload"
                            onChange={(e) => handleFileChange("signature", e)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 pt-4">
              <Link to="/business">
                <Button variant="secondary" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Link>
              <button
                type="submit"
                className="btn btn-custom"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default headerLayoutWrapper(AddBussiness);
